import { Vector3 } from 'three';
import Experience from '../Experience.js';
import Environment from './Environment.js';
import PictureItem from './Items/PictureItem.js';
import Room from './Room.js';
import Events from '../Utils/Events.js';
import Waypoint from '../Controls/Waypoint.js';

export default class World {
    constructor() {
        this.experience = new Experience();
        this.camera = this.experience.camera.instance;
        this.scene = this.experience.scene;
        this.sizes = this.experience.sizes;
        this.resources = this.experience.resources;
        this.selectableItems = [];

        // Debug
        this.debug = this.experience.debug;

        this.generateWaypoints();

        // Wait for resources
        this.resources.eventEmitter.on(Events.RESOURCES_READY, this, () => {
            // Setup
            this.room = new Room();
            this.environment = new Environment();
            this.experience.overlay.hideOverlay(() => {
                this.experience.controls.locomotionArrows.arrowHelper.visible = true;
            });

            // These are the artworks
            // this.generateItems(this.resources.items['roomModel'].children[5].children);
            this.generateItems(Object.values(this.resources.items));
            this.environment.updateAllMaterials();
        });
    }

    generateWaypoints() {
        const waypoint0 = new Waypoint(
            new Vector3(
                this.camera.position.x,
                this.camera.position.y,
                this.camera.position.z
            )
        );
        const waypoint1 = new Waypoint(new Vector3(0, this.camera.position.y, 28.5));
        const waypoint2 = new Waypoint(new Vector3(-9, this.camera.position.y, 28.5));
        const waypoint3 = new Waypoint(new Vector3(-9, this.camera.position.y, 20.3));
        const waypoint4 = new Waypoint(new Vector3(-9, this.camera.position.y, 12.7));
        const waypoint5 = new Waypoint(new Vector3(-9, this.camera.position.y, 4.8));
        const waypoint6 = new Waypoint(new Vector3(-9, this.camera.position.y, -2.5));
        const waypoint7 = new Waypoint(new Vector3(-9, this.camera.position.y, -10.5));
        const waypoint8 = new Waypoint(new Vector3(0, this.camera.position.y, -10.5));
        const waypoint9 = new Waypoint(new Vector3(9, this.camera.position.y, -10.5));

        this.experience.controls.waypoints.push(
            waypoint0,
            waypoint1,
            waypoint2,
            waypoint3,
            waypoint4,
            waypoint5,
            waypoint6,
            waypoint7,
            waypoint8,
            waypoint9
        );
    }

    generateItems(items) {
        for (const item of items) {
            // let observationPoint = new Vector3();
            // if (Math.abs(item.rotation.y) % (2 * Math.PI) < Math.PI / 180) {
            //     // 0 degrees
            //     observationPoint = new Vector3(
            //         item.position.x - 2,
            //         item.position.y,
            //         item.position.z
            //     );
            // } else if (Math.abs(item.rotation.y - Math.PI / 2) % (2 * Math.PI) < Math.PI / 180) {
            //     // 90 degrees
            //     observationPoint = new Vector3(
            //         item.position.x,
            //         item.position.y,
            //         item.position.z + 2
            //     );
            // } else if (Math.abs(item.rotation.y - Math.PI) % (2 * Math.PI) < Math.PI / 180) {
            //     // 180 degrees
            //     observationPoint = new Vector3(
            //         item.position.x + 2,
            //         item.position.y,
            //         item.position.z
            //     );
            // } else if (Math.abs(item.rotation.y - (3 * Math.PI) / 2) % (2 * Math.PI) < Math.PI / 180) {
            //     // 270 degrees
            //     observationPoint = new Vector3(
            //         item.position.x + 2,
            //         item.position.y,
            //         item.position.z
            //     );
            // } else {
            //     console.log('Error: item rotation is not a multiple of 90 degrees');
            // }
            const pictureItem = new PictureItem(
                item.resource.id,
                item.resource.name,
                item.resource.title,
                item.resource.format,
                item.resource.printType,
                item.resource.description,
                item.resource.premio ?? null,
                item.file.scene.children[0],
                item.file.scene.children[0].position,
                new Vector3()
            );
            this.selectableItems.push(pictureItem);
            this.experience.controls.selectableItems.push(item.file.scene.children[0]);
            this.scene.add(item.file.scene.children[0]);
        }

        // Put items in id order
        this.selectableItems.sort((a, b) => parseInt(a.id) - parseInt(b.id));

        // Rocco Rorandelli - id 34
        this.selectableItems[0].observationPoint = new Vector3(
            this.selectableItems[0].originalPosition.x - 1,
            this.selectableItems[0].originalPosition.y,
            this.selectableItems[0].originalPosition.z
        );

        // Manfredi Gioacchini - id 87
        this.selectableItems[1].observationPoint = new Vector3(
            this.selectableItems[1].originalPosition.x,
            this.selectableItems[1].originalPosition.y,
            this.selectableItems[1].originalPosition.z + 1.2
        );

        // Andrea Botto - id 361
        this.selectableItems[2].observationPoint = new Vector3(
            this.selectableItems[2].originalPosition.x,
            this.selectableItems[2].originalPosition.y,
            this.selectableItems[2].originalPosition.z + 1
        );

        // Luca Nostri - id 425
        this.selectableItems[3].observationPoint = new Vector3(
            this.selectableItems[3].originalPosition.x,
            this.selectableItems[3].originalPosition.y,
            this.selectableItems[3].originalPosition.z + 1.1
        );

        // Allegra Martin - id 448
        this.selectableItems[4].observationPoint = new Vector3(
            this.selectableItems[4].originalPosition.x - 0.65,
            this.selectableItems[4].originalPosition.y,
            this.selectableItems[4].originalPosition.z
        );

        // Sabrina Ragucci - id 453
        this.selectableItems[5].observationPoint = new Vector3(
            this.selectableItems[5].originalPosition.x + 0.6,
            this.selectableItems[5].originalPosition.y,
            this.selectableItems[5].originalPosition.z
        );

        // Paolo Ventura - id 460
        this.selectableItems[6].observationPoint = new Vector3(
            this.selectableItems[6].originalPosition.x,
            this.selectableItems[6].originalPosition.y,
            this.selectableItems[6].originalPosition.z - 1.75
        );

        // Marco Cipriani - id 472
        this.selectableItems[7].observationPoint = new Vector3(
            this.selectableItems[7].originalPosition.x,
            this.selectableItems[7].originalPosition.y,
            this.selectableItems[7].originalPosition.z - 0.7
        );

        // Leonardo Magrelli - id 501
        this.selectableItems[8].observationPoint = new Vector3(
            this.selectableItems[8].originalPosition.x + 1.7,
            this.selectableItems[8].originalPosition.y,
            this.selectableItems[8].originalPosition.z
        );

        // Vittorio Mortarotti - id 502
        this.selectableItems[9].observationPoint = new Vector3(
            this.selectableItems[9].originalPosition.x,
            this.selectableItems[9].originalPosition.y,
            this.selectableItems[9].originalPosition.z - 1.4
        );

        // Fabio Barile - id 523
        this.selectableItems[10].observationPoint = new Vector3(
            this.selectableItems[10].originalPosition.x,
            this.selectableItems[10].originalPosition.y,
            this.selectableItems[10].originalPosition.z + 1.5
        );

        // Giovanna Silva - id 539
        this.selectableItems[11].observationPoint = new Vector3(
            this.selectableItems[11].originalPosition.x + 1.75,
            this.selectableItems[11].originalPosition.y,
            this.selectableItems[11].originalPosition.z
        );

        // Mohamed Keita - id 554
        this.selectableItems[12].observationPoint = new Vector3(
            this.selectableItems[12].originalPosition.x + 1.7,
            this.selectableItems[12].originalPosition.y,
            this.selectableItems[12].originalPosition.z
        );

        // Olivo Barbieri - id 558
        this.selectableItems[13].observationPoint = new Vector3(
            this.selectableItems[13].originalPosition.x,
            this.selectableItems[13].originalPosition.y,
            this.selectableItems[13].originalPosition.z - 0.5
        );

        // Luca Campigotto - id 576
        this.selectableItems[14].observationPoint = new Vector3(
            this.selectableItems[14].originalPosition.x,
            this.selectableItems[14].originalPosition.y,
            this.selectableItems[14].originalPosition.z - 1.5
        );

        // Stefano Graziani - id 593
        this.selectableItems[15].observationPoint = new Vector3(
            this.selectableItems[15].originalPosition.x,
            this.selectableItems[15].originalPosition.y,
            this.selectableItems[15].originalPosition.z + 2
        );

        // Jacopo Valentini - id 594
        this.selectableItems[16].observationPoint = new Vector3(
            this.selectableItems[16].originalPosition.x,
            this.selectableItems[16].originalPosition.y,
            this.selectableItems[16].originalPosition.z - 1.4
        );

        // Antonio Biasiucci - id 652
        this.selectableItems[17].observationPoint = new Vector3(
            this.selectableItems[17].originalPosition.x,
            this.selectableItems[17].originalPosition.y,
            this.selectableItems[17].originalPosition.z - 0.45
        );

        // Dimitri Angelini - id 675
        this.selectableItems[18].observationPoint = new Vector3(
            this.selectableItems[18].originalPosition.x,
            this.selectableItems[18].originalPosition.y,
            this.selectableItems[18].originalPosition.z - 1.25
        );

        // Raffaela Mariniello - id 682
        this.selectableItems[19].observationPoint = new Vector3(
            this.selectableItems[19].originalPosition.x - 1.6,
            this.selectableItems[19].originalPosition.y,
            this.selectableItems[19].originalPosition.z
        );

        // Guglielmo Cherchi - id 724
        this.selectableItems[20].observationPoint = new Vector3(
            this.selectableItems[20].originalPosition.x + 1.35,
            this.selectableItems[20].originalPosition.y,
            this.selectableItems[20].originalPosition.z
        );

        // Eva Frapiccini - id 728
        this.selectableItems[21].observationPoint = new Vector3(
            this.selectableItems[21].originalPosition.x + 1.25,
            this.selectableItems[21].originalPosition.y,
            this.selectableItems[21].originalPosition.z
        );

        // Vincenzo Pagliuca - id 772
        this.selectableItems[22].observationPoint = new Vector3(
            this.selectableItems[22].originalPosition.x - 0.5,
            this.selectableItems[22].originalPosition.y,
            this.selectableItems[22].originalPosition.z
        );

        // Nicola Baldazzi - id 806
        this.selectableItems[23].observationPoint = new Vector3(
            this.selectableItems[23].originalPosition.x - 1,
            this.selectableItems[23].originalPosition.y,
            this.selectableItems[23].originalPosition.z
        );

        // Stephen Roach - id 843
        this.selectableItems[24].observationPoint = new Vector3(
            this.selectableItems[24].originalPosition.x,
            this.selectableItems[24].originalPosition.y,
            this.selectableItems[24].originalPosition.z + 1.5
        );

        // Luca Spano - id 859
        this.selectableItems[25].observationPoint = new Vector3(
            this.selectableItems[25].originalPosition.x + 1.55,
            this.selectableItems[25].originalPosition.y,
            this.selectableItems[25].originalPosition.z
        );

        // Francesco Neri - id 875
        this.selectableItems[26].observationPoint = new Vector3(
            this.selectableItems[26].originalPosition.x,
            this.selectableItems[26].originalPosition.y,
            this.selectableItems[26].originalPosition.z + 1.1
        );

        // Martina Cirese - id 879
        this.selectableItems[27].observationPoint = new Vector3(
            this.selectableItems[27].originalPosition.x - 1.4,
            this.selectableItems[27].originalPosition.y,
            this.selectableItems[27].originalPosition.z
        );

        // Margherita Del Piano - id 987
        this.selectableItems[28].observationPoint = new Vector3(
            this.selectableItems[28].originalPosition.x,
            this.selectableItems[28].originalPosition.y,
            this.selectableItems[28].originalPosition.z + 1.6
        );

        // Stefano Cagol - id 1117
        this.selectableItems[29].observationPoint = new Vector3(
            this.selectableItems[29].originalPosition.x + 1.25,
            this.selectableItems[29].originalPosition.y,
            this.selectableItems[29].originalPosition.z
        );

        // Lorenzo Castore - id 1130
        this.selectableItems[30].observationPoint = new Vector3(
            this.selectableItems[30].originalPosition.x,
            this.selectableItems[30].originalPosition.y,
            this.selectableItems[30].originalPosition.z - 2
        );

        // Matteo Lisanti - id 1216
        this.selectableItems[31].observationPoint = new Vector3(
            this.selectableItems[31].originalPosition.x,
            this.selectableItems[31].originalPosition.y,
            this.selectableItems[31].originalPosition.z + 1.7
        );

        // Federico Calogero - id 1219
        this.selectableItems[32].observationPoint = new Vector3(
            this.selectableItems[32].originalPosition.x,
            this.selectableItems[32].originalPosition.y,
            this.selectableItems[32].originalPosition.z - 2.9
        );

        // Camilla Postiglione - id 1246
        this.selectableItems[33].observationPoint = new Vector3(
            this.selectableItems[33].originalPosition.x,
            this.selectableItems[33].originalPosition.y,
            this.selectableItems[33].originalPosition.z + 0.8
        );

        // Daniele Molajoli - id 1284
        this.selectableItems[34].observationPoint = new Vector3(
            this.selectableItems[34].originalPosition.x + 1,
            this.selectableItems[34].originalPosition.y,
            this.selectableItems[34].originalPosition.z
        );

        // Alessandro Dandini de Sylva - id 1292
        this.selectableItems[35].observationPoint = new Vector3(
            this.selectableItems[35].originalPosition.x,
            this.selectableItems[35].originalPosition.y,
            this.selectableItems[35].originalPosition.z + 1.4
        );

        // Flavio Scollo - id 1335
        this.selectableItems[36].observationPoint = new Vector3(
            this.selectableItems[36].originalPosition.x + 1.3,
            this.selectableItems[36].originalPosition.y,
            this.selectableItems[36].originalPosition.z
        );

        // Margherita Nuti - id 1360
        this.selectableItems[37].observationPoint = new Vector3(
            this.selectableItems[37].originalPosition.x + 1.25,
            this.selectableItems[37].originalPosition.y,
            this.selectableItems[37].originalPosition.z
        );

        // Gaia Renis - id 1377
        this.selectableItems[38].observationPoint = new Vector3(
            this.selectableItems[38].originalPosition.x - 1,
            this.selectableItems[38].originalPosition.y,
            this.selectableItems[38].originalPosition.z
        );

        // Karmen Corak - id 1405
        this.selectableItems[39].observationPoint = new Vector3(
            this.selectableItems[39].originalPosition.x,
            this.selectableItems[39].originalPosition.y,
            this.selectableItems[39].originalPosition.z + 0.6
        );

        // Debug
        // for (let i = 0; i < this.selectableItems.length; i++) {
        //     console.log(i, this.selectableItems[i].artData.name);
        //     console.log(this.selectableItems[i].observationPoint);
        // }
    }

    destroy() {
        this.resources.eventEmitter.off(Events.RESOURCES_READY, this);
        this.selectableItems.forEach((item) => {
            item.destroy();
        });
    }
}
